import React from "react";
import { Button, Tooltip, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis
} from "recharts";

import {
  BLOCKS, ChartTooltipBox, DataTable, EmptyState, OsLink, Panel, SeverityLabel, StatCard,
  axisProps, blockColor, chartInk, dateLabel, daysLabel, legendFormatter, seriesColor, typeColor
} from "./shared";

const MAX_STAGE = 13;

const useStyles = makeStyles(theme => ({
  grid: { display: "grid", gap: theme.spacing(2), gridTemplateColumns: "minmax(0, 1fr)" },
  cards: {
    display: "grid", gap: theme.spacing(1.5),
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))"
  },
  legend: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5, 2), marginBottom: theme.spacing(1.5) },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.palette.text.secondary },
  swatch: { width: 10, height: 10, borderRadius: 2 },
  stageRow: {
    display: "grid", gridTemplateColumns: "minmax(140px, 220px) minmax(0, 1fr) 150px",
    alignItems: "center", gap: theme.spacing(1.5), padding: theme.spacing(0.75, 0),
    "& + &": { borderTop: `1px solid ${theme.palette.divider}` },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)", gap: theme.spacing(0.5) }
  },
  projectButton: {
    display: "flex", width: "100%", maxWidth: "100%", minWidth: 0,
    justifyContent: "flex-start", textAlign: "left", textTransform: "none", padding: theme.spacing(0.25, 0.5),
    "& .MuiButton-label": {
      display: "block", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
    }
  },
  projectMeta: { fontSize: 11, color: theme.palette.text.secondary, paddingLeft: theme.spacing(0.5) },
  track: { position: "relative", height: 14, borderRadius: 4, background: theme.palette.action.hover },
  fill: { position: "absolute", top: 0, bottom: 0, left: 0, borderRadius: 4, minWidth: 4 },
  ticks: { position: "relative", height: 14, fontSize: 10, color: theme.palette.text.secondary, marginTop: 2 },
  tick: { position: "absolute", transform: "translateX(-100%)" },
  stageInfo: { minWidth: 0, overflowWrap: "anywhere" },
  stageLabel: { fontSize: 12, lineHeight: 1.3 },
  stageStatus: { fontSize: 11, color: theme.palette.text.secondary }
}));

const CurrentStageChart = ({ rows, onOpenProject }) => {
  const classes = useStyles();
  const theme = useTheme();
  if (!rows.length) return <EmptyState>Nenhum projeto ativo com os filtros selecionados.</EmptyState>;

  return (
    <>
      <div className={classes.legend} aria-label="Legenda dos blocos">
        {BLOCKS.map(block => (
          <span key={block} className={classes.legendItem}>
            <span className={classes.swatch} style={{ background: blockColor(theme, block) }} />{block}
          </span>
        ))}
      </div>
      {rows.map(row => {
        const stage = row.etapa_atual;
        const position = stage?.numero ? Math.min(stage.numero, MAX_STAGE) : (row.percentual === 100 ? MAX_STAGE : 0);
        const color = stage ? blockColor(theme, stage.bloco) : chartInk(theme).muted;
        return (
          <div key={row.projeto_id} className={classes.stageRow}>
            <div style={{ minWidth: 0 }}>
              <Button size="small" className={classes.projectButton} onClick={() => onOpenProject(row.projeto_id)}
                title={`Abrir projeto ${row.cliente}`}>
                {row.cliente}
              </Button>
              <div className={classes.projectMeta}>#{row.projeto_id} · {row.tipo || "Sem tipo"} · {row.status}</div>
            </div>
            <Tooltip arrow title={stage
              ? `${stage.rotulo} · ${stage.bloco || "Sem bloco"} · ${stage.status} · ${row.etapas_concluidas}/${row.etapas_total} etapas concluídas`
              : `${row.etapas_concluidas}/${row.etapas_total} etapas concluídas`}>
              <div>
                <div className={classes.track} role="img"
                  aria-label={`Etapa atual ${stage?.rotulo || "nenhuma"}`}>
                  <div className={classes.fill} style={{ width: `${(position / MAX_STAGE) * 100}%`, background: color }} />
                </div>
                <div className={classes.ticks} aria-hidden="true">
                  {[1, 7, 13].map(tick => (
                    <span key={tick} className={classes.tick} style={{ left: `${(tick / MAX_STAGE) * 100}%` }}>
                      {String(tick).padStart(2, "0")}
                    </span>
                  ))}
                </div>
              </div>
            </Tooltip>
            <div className={classes.stageInfo}>
              <div className={classes.stageLabel}>{stage ? stage.rotulo : "Etapas concluídas"}</div>
              <div className={classes.stageStatus}>
                {stage ? stage.status : ""}{row.etapas_atrasadas ? ` · ${row.etapas_atrasadas} atrasada(s)` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

const WorkloadChart = ({ rows }) => {
  const theme = useTheme();
  if (!rows.length) return <EmptyState>Nenhuma OS aberta nos projetos filtrados.</EmptyState>;
  const height = Math.max(160, rows.length * 34 + 60);
  const ink = chartInk(theme);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }} barCategoryGap={8}>
        <CartesianGrid horizontal={false} stroke={ink.grid} />
        <XAxis type="number" allowDecimals={false} {...axisProps(theme)} />
        <YAxis type="category" dataKey="tecnico" width={120} {...axisProps(theme)} />
        <ChartTooltip cursor={{ fill: theme.palette.action.hover }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={payload[0].payload.tecnico} rows={[
            { label: "Etapas", value: payload[0].payload.etapas, color: seriesColor(theme, 0) },
            { label: "Customizações", value: payload[0].payload.customizacoes, color: seriesColor(theme, 1) },
            { label: "Total", value: payload[0].payload.total }
          ]} />
        ) : null)} />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={legendFormatter(theme)} />
        <Bar dataKey="etapas" name="Etapas (grupo 3)" stackId="carga" fill={seriesColor(theme, 0)}
          stroke={theme.palette.background.paper} strokeWidth={2} maxBarSize={24} />
        <Bar dataKey="customizacoes" name="Customizações (grupo 2)" stackId="carga" fill={seriesColor(theme, 1)}
          stroke={theme.palette.background.paper} strokeWidth={2} radius={[0, 4, 4, 0]} maxBarSize={24} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const PortfolioTab = ({ data, onOpenProject }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { portfolio, options } = data;

  return (
    <div className={classes.grid}>
      <div className={classes.cards}>
        {portfolio.active_by_type.map(item => (
          <StatCard key={item.tipo} label={`Em execução · ${item.tipo}`} value={item.total}
            accent={typeColor(theme, options.tipos, item.tipo)} />
        ))}
        <StatCard label="Vendidos aguardando início" value={portfolio.waiting_start}
          detail="Projetos com status Aprovado" accent={theme.palette.text.secondary} />
      </div>

      <Panel title="Etapa atual de cada projeto"
        subtitle="Projetos não concluídos nem cancelados. A barra avança até o número da etapa atual (01 a 13), na cor do bloco.">
        <CurrentStageChart rows={portfolio.current_stage} onOpenProject={onOpenProject} />
      </Panel>

      <Panel title="Etapas atrasadas" subtitle="Previsão de término anterior a hoje e status diferente de Concluída e Cancelado.">
        <DataTable
          rows={portfolio.late_stages}
          rowKey={row => row.os_id}
          emptyText="Nenhuma etapa atrasada."
          maxHeight={420}
          columns={[
            { key: "semaforo", label: "Situação", render: row => <SeverityLabel level={row.semaforo} /> },
            { key: "dias_atraso", label: "Atraso", numeric: true, render: row => daysLabel(row.dias_atraso) },
            { key: "os_id", label: "OS", render: row => <OsLink id={row.os_id} /> },
            { key: "cliente", label: "Cliente" },
            { key: "rotulo", label: "Etapa" },
            { key: "tecnico", label: "Técnico", render: row => row.tecnico || "-" },
            { key: "status", label: "Status" },
            { key: "previsao_termino", label: "Previsão", render: row => dateLabel(row.previsao_termino) }
          ]}
        />
      </Panel>
      <Panel title="Carga por técnico" subtitle="OS filhas abertas dos grupos 3 e 2 em projetos ativos.">
        <WorkloadChart rows={portfolio.workload} />
        <Typography variant="caption" color="textSecondary">
          {portfolio.workload.reduce((sum, row) => sum + row.total, 0)} OS abertas no total.
        </Typography>
      </Panel>
    </div>
  );
};

export default PortfolioTab;
